/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import DownloadIcon from "@mui/icons-material/Download";
import { Alert, IconButton, Snackbar, Tooltip } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { PartItem, UIDPartApi, UIDPartTicketDetails, UIDPartTicketMapping, WarehouseItem } from "../../../api/UID-Part/UIDPartApi";
import { MEDIA_BASE_URL } from "../../../config/apiConfig";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";

interface UIDPartMapping {
  ticketId: number;
  uid: string | null;
  uidStatus: string;
  fleetName: string;
  fleetType: string;
  fleetStatus?: string;
  createdAt: string;
  component: string;
  partState: string;
}

interface BulkUploadHistoryItem {
  id: number;
  fileName: string;
  uploadedDate: string;
  uploadedFileUrl: string;
  errorResponseSheetUrl: string;
  totalCount: number;
  uploadedBy: string;
  totalRecords: number;
  failRecords: number;
  successRecords: number;
}


const PARTS_PAGE_SIZE = 20;

const UIDPartCodeMapping = () => {
  const { setLoading } = useLoading();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>(searchQuery);
  const [mappingData, setMappingData] = useState<UIDPartMapping[]>([]);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [ticketDetails, setTicketDetails] = useState<UIDPartTicketDetails | null>(null);

  // ── Parts dropdown state ──────────────────────────────────────────────────
  const [partsList, setPartsList] = useState<PartItem[]>([]);
  const [partsPage, setPartsPage] = useState(0);
  const [partsTotalPages, setPartsTotalPages] = useState(1);
  const [partCodeInputValue, setPartCodeInputValue] = useState("");
  const [partNameInputValue, setPartNameInputValue] = useState("");
  const [partsSearch, setPartsSearch] = useState("");
  const [partsLoading, setPartsLoading] = useState(false);
  const partsSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const partsPageRef = useRef(0);
  const partsTotalPagesRef = useRef(1);
  const partsSearchRef = useRef("");
  const partsLoadingRef = useRef(false);
  // ─────────────────────────────────────────────────────────────────────────

  const [selectedPartCode, setSelectedPartCode] = useState<string>("");
  const [selectedPartName, setSelectedPartName] = useState<string>("");
  const [selectedPartId, setSelectedPartId] = useState<number>(0);
  const [onboardingDate, setOnboardingDate] = useState<string>("");
  const [expiryDate, setExpiryDate] = useState<string>("");
  const [warehouseList, setWarehouseList] = useState<WarehouseItem[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [selectedUID, setSelectedUID] = useState<string>("");
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'mapped' | 'bulk-upload'>('pending');
  const [bulkUploadHistory, setBulkUploadHistory] = useState<BulkUploadHistoryItem[]>([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const [cancelRemarks, setCancelRemarks] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const showSnackbar = (message: string, severity: "success" | "error" = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ── Parts API helpers ─────────────────────────────────────────────────────
  const fetchParts = useCallback(async (pg: number, search: string, append: boolean) => {
    partsLoadingRef.current = true;
    setPartsLoading(true);
    try {
      const res = await UIDPartApi.api.getPartsList(pg, PARTS_PAGE_SIZE, 2, search || undefined);
      const items: PartItem[] = res.data?.data ?? [];
      const total: number = res.data?.totalPages ?? 1;
      partsTotalPagesRef.current = total;
      setPartsTotalPages(total);
      partsPageRef.current = pg;
      setPartsPage(pg);
      setPartsList((prev) => (append ? [...prev, ...items] : items));
    } catch (err) {
      console.error("Error fetching parts", err);
    } finally {
      partsLoadingRef.current = false;
      setPartsLoading(false);
    }
  }, []);

  const triggerPartsSearch = useCallback((inputValue: string) => {
    partsPageRef.current = 0;
    setPartsPage(0);
    if (partsSearchTimer.current) clearTimeout(partsSearchTimer.current);
    partsSearchTimer.current = setTimeout(() => {
      partsSearchRef.current = inputValue;
      setPartsSearch(inputValue);
      fetchParts(0, inputValue, false);
    }, 400);
  }, [fetchParts]);

  const handlePartCodeInputChange = useCallback((inputValue: string) => {
    setPartCodeInputValue(inputValue);
    triggerPartsSearch(inputValue);
  }, [triggerPartsSearch]);

  const handlePartNameInputChange = useCallback((inputValue: string) => {
    setPartNameInputValue(inputValue);
    triggerPartsSearch(inputValue);
  }, [triggerPartsSearch]);

  const handlePartsMenuScrollToBottom = useCallback(() => {
    const nextPage = partsPageRef.current + 1;
    if (nextPage < partsTotalPagesRef.current && !partsLoadingRef.current) {
      fetchParts(nextPage, partsSearchRef.current, true);
    }
  }, [fetchParts]);
  // ─────────────────────────────────────────────────────────────────────────

  type PartNameOption = {
    value: string;
    label: string;
    id: number;
    referenceCode: string;
  };

  type PartOption = {
    value: string;
    label: string;
    id: number;
    description: string;
  };

  const partOptions: PartOption[] = partsList.map((part) => ({
    value: part.referenceCode,
    label: `${part.referenceCode} - ${part.description}`,
    id: part.id,
    description: part.description,
  }));

  const partNameOptions: PartNameOption[] = partsList.map((part) => ({
    value: part.description,
    label: part.description,
    id: part.id,
    referenceCode: part.referenceCode,
  }));

  const handleViewUidImage = () => {
    if (ticketDetails?.uidImageId) {
      const fileUrl = `${MEDIA_BASE_URL}/${ticketDetails.uidImageId}`;
      setImagePreviewUrl(fileUrl);
    }
  };

  const DetailField = ({ label, value }: { label: string; value?: string }) => (
    <div className="flex flex-col">
      <div className="text-xs text-gray-500 font-medium mb-2 text-left">{label}</div>
      <div className="text-sm text-gray-900 pb-2 border-b border-gray-200 text-left">
        {value || "-"}
      </div>
    </div>
  );

  const selectClassNames = {
    control: () =>
      "flex items-center border-b border-gray-200 bg-transparent min-h-[38px] focus-within:border-gray-400",
    valueContainer: () => "flex-1 px-0 py-0",
    input: () => "text-sm text-gray-900",
    placeholder: () => "text-sm text-gray-400",
    singleValue: () => "text-sm text-gray-900",
    indicatorsContainer: () => "flex items-center",
    dropdownIndicator: () => "text-gray-900",
    indicatorSeparator: () => "hidden",
    menu: () =>
      "mt-2 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-50",
    option: ({ isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) =>
      `px-3 py-2 text-sm cursor-pointer ${isSelected ? "bg-gray-100 font-medium" : isFocused ? "bg-gray-50" : "bg-white"
      }`,
    noOptionsMessage: () => "px-3 py-2 text-sm text-gray-500",
    loadingMessage: () => "px-3 py-2 text-sm text-gray-400",
  };

  const handleMapPart = async () => {
    if (!selectedPartCode) {
      showSnackbar("Please select a part code", "error");
      return;
    }
    try {
      setLoading(true);
      const response = await UIDPartApi.api.mapPartToTicket(
        expandedRow!,
        selectedPartId,
        onboardingDate || "",
        expiryDate || ""
      );
      showSnackbar((response as any)?.message || "Part mapped successfully", "success");
      await refetchMappingList();
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.message || error?.message || "Failed to map part. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCancelDialog = () => {
    if (!selectedPartCode) {
      handleCancelTicket();
      return;
    }
    setShowCancelDialog(true);
  };

  const handleCancelTicket = async () => {
    try {
      setLoading(true);
      if (showCancelDialog) setShowCancelDialog(false);
      const response = await UIDPartApi.api.cancelPartMapTicket(
        expandedRow!,
        selectedPartId,
        onboardingDate || "",
        expiryDate || ""
      );
      showSnackbar((response as any)?.message || "Ticket cancelled successfully", "success");
      await refetchMappingList();
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.message || error?.message || "Failed to cancel ticket. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const refetchMappingList = async () => {
    const listResponse = await UIDPartApi.api.getUIDPartTicketMappings(
      page,
      rowsPerPage,
      searchQuery || undefined,
      selectedUID || undefined,
      selectedWarehouse ? parseInt(selectedWarehouse, 10) : undefined,
      activeTab === 'pending' ? 'PENDING' : 'COMPLETED'
    );
    const payload: UIDPartTicketMapping[] = listResponse?.data?.partMapTickets ?? [];
    setMappingData(payload);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      await UIDPartApi.api.downloadUIDPartTicketMappings(
        searchQuery || undefined,
        selectedUID || undefined,
        selectedWarehouse ? parseInt(selectedWarehouse, 10) : undefined,
        activeTab === 'pending' ? 'PENDING' : 'COMPLETED'
      );
    } catch (error: any) {
      console.error("Download failed", error);
      showSnackbar(
        error?.message || "Failed to download report. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: any) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      const uploadResponse = await UIDPartApi.api.uploadFile(file);
      const fileId = uploadResponse?.data?.fileId || (uploadResponse as any)?.fileId;
      const fileName = uploadResponse?.data?.fileName || (uploadResponse as any)?.fileName || file.name;
      if (!fileId) {
        throw new Error("Failed to get fileId from server response");
      }
      const response = await UIDPartApi.api.bulkPartMappingUpload(fileId, fileName);
      showSnackbar(response?.data);
      await refetchMappingList();
    } catch (error: any) {
      console.error("Bulk upload failed", error);
      showSnackbar(
        error?.response?.data?.message || error?.message || "Bulk upload failed. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleSnackbarClose = (_?: unknown, reason?: string) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const toggleRow = async (ticketId: number) => {
    if (expandedRow === ticketId) {
      setExpandedRow(null);
      setTicketDetails(null);
      setSelectedPartCode("");
      setSelectedPartName("");
      setSelectedPartId(0);
      setOnboardingDate("");
      setExpiryDate("");
      setPartsList([]);
      setPartsPage(0);
      setPartsSearch("");
      setPartCodeInputValue("");
      setPartNameInputValue("");
      partsPageRef.current = 0;
      partsSearchRef.current = "";
      partsTotalPagesRef.current = 1;
    } else {
      setExpandedRow(ticketId);
      try {
        setLoading(true);
        const [detailsResponse, partsResponse] = await Promise.all([
          UIDPartApi.api.getUIDPartTicketDetails(ticketId),
          UIDPartApi.api.getPartsList(0, PARTS_PAGE_SIZE, 2),
        ]);
        setTicketDetails(detailsResponse.data);
        const items: PartItem[] = partsResponse.data?.data ?? [];
        setPartsList(items);
        setPartsPage(0);
        setPartsTotalPages(partsResponse.data?.totalPages ?? 1);
        setPartsSearch("");
        partsPageRef.current = 0;
        partsTotalPagesRef.current = partsResponse.data?.totalPages ?? 1;
        partsSearchRef.current = "";
        const partCode = detailsResponse.data.referenceCode || detailsResponse.data.partCode || "";
        setSelectedPartCode(partCode);
        setSelectedPartName(detailsResponse.data.partName || "");
        const foundPart = items.find((p: PartItem) => p.referenceCode === partCode);
        if (foundPart) setSelectedPartId(foundPart.id);
        if (detailsResponse.data.onBoardingDate) {
          setOnboardingDate(detailsResponse.data.onBoardingDate.split("T")[0]);
        }
        if (detailsResponse.data.expiryDate) {
          setExpiryDate(detailsResponse.data.expiryDate.split("T")[0]);
        }
      } catch (error) {
        console.error("Error fetching ticket details", error);
        setTicketDetails(null);
        setPartsList([]);
      } finally {
        setLoading(false);
      }
    }
  };

  // ✅ Accept page params explicitly to avoid stale closure
  const fetchBulkUploadHistory = async (currentPage: number, currentPageSize: number , purpose:string) => {
    try {
      setLoading(true);
      const response = await UIDPartApi.api.getBulkUploadHistory(currentPage, currentPageSize ,purpose);
      const historyItems = response.data?.bulkUploadHistory ?? [];
      setBulkUploadHistory(historyItems);
      // ✅ Use hasNext flag from response instead of totalPages
      setHasNextPage(response.data?.hasNext ?? false);
    } catch (error) {
      console.error("Error fetching bulk upload history", error);
      setBulkUploadHistory([]);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchMappingData = async () => {
      setLoading(true);
      try {
        if (activeTab === 'bulk-upload') {
          // ✅ Pass fresh page/rowsPerPage directly — no stale closure
          await fetchBulkUploadHistory(page, rowsPerPage,"UID-PART-MAP");
          return;
        }

        const [mappingResponse, warehouseResponse] = await Promise.all([
          UIDPartApi.api.getUIDPartTicketMappings(
            page,
            rowsPerPage,
            debouncedSearch || undefined,
            selectedUID || undefined,
            selectedWarehouse ? parseInt(selectedWarehouse, 10) : undefined,
            activeTab === 'pending' ? 'PENDING' : 'COMPLETED'
          ),
          UIDPartApi.api.getWarehousesList(0, 100),
        ]);
        setMappingData(mappingResponse?.data?.partMapTickets ?? []);
        setWarehouseList(warehouseResponse?.data?.data ?? []);
        setTotalPages(mappingResponse?.data?.totalPages ?? 0);
      } catch (error) {
        console.error("Error fetching data", error);
        setMappingData([]);
        setWarehouseList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMappingData();
  }, [page, rowsPerPage, debouncedSearch, selectedUID, selectedWarehouse, activeTab]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    return () => {
      if (partsSearchTimer.current) clearTimeout(partsSearchTimer.current);
    };
  }, []);

  return (
    <div className="flex h-screen">
      <div className="w-64 bg-white shadow-md fixed left-0 top-0 bottom-0">
        <SideBarNavigation />
      </div>
      <div className="flex-1 ml-64 p-8 bg-[#F8FAFC] overflow-auto">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold mb-8 text-gray-900 text-left">
            UID-Part code Mapping
          </h1>
          <div className="flex gap-2">
            <label className="px-5 py-2 bg-green-600 text-white text-sm rounded-full font-medium hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" />
              </svg>
              Bulk Upload
              <input
                type="file"
                accept=".xlsx,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-0">
          <button
            onClick={() => { setActiveTab('pending'); setPage(0); setHasNextPage(false); }}
            className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${activeTab === 'pending' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            PENDING REQUESTS
            {activeTab === 'pending' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600" />}
          </button>
          <button
            onClick={() => { setActiveTab('mapped'); setPage(0); setHasNextPage(false); }}
            className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${activeTab === 'mapped' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            COMPLETED REQUESTS
            {activeTab === 'mapped' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600" />}
          </button>
          <button
            onClick={() => { setActiveTab('bulk-upload'); setPage(0); }}
            className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${activeTab === 'bulk-upload' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            BULK UPLOAD HISTORY
            {activeTab === 'bulk-upload' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600" />}
          </button>
        </div>

        {/* Search and Filter Section */}
        {activeTab !== 'bulk-upload' && (
          <div className="bg-white rounded-t-lg p-5 mb-0 shadow-sm border border-gray-200 border-b-0 border-t-0">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 relative max-w-md">
                <input
                  type="text"
                  placeholder="Search by uid"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
                />
                <svg className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 text-sm font-medium">UID :</span>
                  <select
                    value={selectedUID}
                    onChange={(e) => setSelectedUID(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left"
                  >
                    <option value="">All</option>
                    <option value="BATTERY">Battery</option>
                    <option value="MOTOR">Motor</option>
                    <option value="CHARGER">Charger</option>
                    <option value="CONTROLLER">Controller</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 text-sm font-medium">Hub:</span>
                  <select
                    value={selectedWarehouse}
                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left"
                  >
                    <option value="">Select Hub</option>
                    {warehouseList.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                    ))}
                  </select>
                </div>
                <button onClick={handleDownload} className="px-5 py-2 bg-blue-600 text-white text-sm rounded-full font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className="bg-white shadow-sm overflow-hidden border border-gray-200 rounded-b-lg border-t-0">
          <div className="overflow-x-auto">
            {activeTab === 'bulk-upload' ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">ID</th>
                    <th className="px-16 py-4 text-left text-sm font-semibold text-gray-700">File</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700">Upload Sheet URL</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700">Error Sheet URL</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Upload Date</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Uploaded By</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700">Total</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700">Success</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkUploadHistory.length > 0 ? (
                    bulkUploadHistory.map((item, index) => (
                      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 text-sm text-gray-700">{item.id}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 font-medium">{item.fileName}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 font-medium text-center">
                          <Tooltip title="Download Uploaded File">
                            <IconButton
                              onClick={() => window.open(item.uploadedFileUrl, '_blank')}
                              color="primary"
                              size="small"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 font-medium text-center">
                          {item.errorResponseSheetUrl ? (
                            <Tooltip title="Download Error Sheet">
                              <IconButton
                                onClick={() => window.open(item.errorResponseSheetUrl, '_blank')}
                                color="error"
                                size="small"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <span className="text-gray-400 italic">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">{item.uploadedDate}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 font-medium">{item.uploadedBy ?? "-"}</td>
                        <td className="px-4 py-4 text-sm text-gray-700 font-medium text-center">{item.totalRecords ?? 0}</td>
                        <td className="px-4 py-4 text-sm text-green-600 font-medium text-center">{item.successRecords ?? 0}</td>
                        <td className="px-4 py-4 text-sm text-red-600 font-medium text-center">{item.failRecords ?? 0}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="px-6 py-8 text-center text-gray-500">No bulk upload history available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Ticket ID</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">UID Number</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">UID Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Fleet Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Fleet Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Component</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Created At</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mappingData.length > 0 ? (
                    mappingData.map((item, index) => (
                      <>
                        <tr key={index} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-700">{item.ticketId}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.uid}</td>
                          <td className="px-5 py-4 text-sm">
                            <span className={`px-1 py-1 rounded-full text-xs font-medium whitespace-nowrap ${item.uidStatus === "Pending" ? "bg-yellow-100 text-yellow-700"
                              : item.uidStatus === "Part Mapped" ? "bg-green-100 text-green-700"
                                : item.uidStatus === "Cancelled" ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}>
                              {item.uidStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.fleetName}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.fleetType}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.component || "-"}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.createdAt}</td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => toggleRow(item.ticketId)}
                              aria-label={expandedRow === item.ticketId ? "Collapse details" : "Expand details"}
                              className="inline-flex items-center justify-center w-10 h-8 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                              {expandedRow === item.ticketId ? (
                                <svg className="w-7 h-7 text-blue-600" viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M12 9l5 6H7l5-6z" fill="currentColor" />
                                </svg>
                              ) : (
                                <svg className="w-7 h-7 text-blue-600" viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M12 15l-5-6h10l-5 6z" fill="currentColor" />
                                </svg>
                              )}
                            </button>
                          </td>
                        </tr>

                        {expandedRow === item.ticketId && (
                          <tr className="bg-gray-50">
                            <td colSpan={9} className="px-6">
                              <div className="bg-white rounded-lg border border-blue-500 p-6">
                                <div className="mb-6">
                                  <h3 className="text-base font-semibold text-gray-900 text-left">Ticket Details</h3>
                                </div>

                                <div className="grid grid-cols-4 gap-x-16 gap-y-8 items-end">

                                  {/* ── Reference Code ─────────────────────────── */}
                                  {activeTab === 'mapped' ? (
                                    <DetailField label="Reference Code" value={selectedPartCode} />
                                  ) : (
                                    <div className="flex flex-col">
                                      <label className="text-xs text-gray-500 font-medium mb-2 block text-left">
                                        Reference Code <span className="text-red-500">*</span>
                                      </label>
                                      <Select<PartOption, false>
                                        options={partOptions}
                                        value={selectedPartCode ? (partOptions.find((o) => o.value === selectedPartCode) || null) : null}
                                        onChange={(opt) => {
                                          if (!opt) return;
                                          setSelectedPartCode(opt.value);
                                          setSelectedPartName(opt.description);
                                          setSelectedPartId(opt.id);
                                          setPartCodeInputValue("");
                                        }}
                                        onInputChange={(val, { action }) => {
                                          if (action === "input-change") handlePartCodeInputChange(val);
                                        }}
                                        inputValue={partCodeInputValue}
                                        onMenuScrollToBottom={handlePartsMenuScrollToBottom}
                                        isLoading={partsLoading}
                                        filterOption={() => true}
                                        placeholder="Select Part Code"
                                        isSearchable
                                        unstyled
                                        classNames={selectClassNames}
                                      />
                                    </div>
                                  )}

                                  {/* ── Part Name ──────────────────────────────── */}
                                  {activeTab === 'mapped' ? (
                                    <DetailField label="Part Name" value={selectedPartName} />
                                  ) : (
                                    <div className="flex flex-col">
                                      <label className="text-xs text-gray-500 font-medium mb-2 block text-left">
                                        Part Name
                                      </label>
                                      <Select<PartNameOption, false>
                                        options={partNameOptions}
                                        value={selectedPartName ? (partNameOptions.find((o) => o.value === selectedPartName) || null) : null}
                                        onChange={(opt) => {
                                          if (!opt) return;
                                          setSelectedPartName(opt.value);
                                          setSelectedPartCode(opt.referenceCode);
                                          setSelectedPartId(opt.id);
                                          setPartNameInputValue("");
                                        }}
                                        onInputChange={(val, { action }) => {
                                          if (action === "input-change") handlePartNameInputChange(val);
                                        }}
                                        inputValue={partNameInputValue}
                                        onMenuScrollToBottom={handlePartsMenuScrollToBottom}
                                        isLoading={partsLoading}
                                        filterOption={() => true}
                                        placeholder="Select Part Name"
                                        isSearchable
                                        unstyled
                                        classNames={selectClassNames}
                                      />
                                    </div>
                                  )}

                                  {/* ── Onboarding Date ────────────────────────── */}
                                  {activeTab === 'pending' && !ticketDetails?.onBoardingDate ? (
                                    <div className="flex flex-col">
                                      <label className="text-xs text-gray-500 font-medium mb-2 block text-left">Onboarding Date</label>
                                      <input
                                        type="date"
                                        value={onboardingDate}
                                        onChange={(e) => setOnboardingDate(e.target.value)}
                                        className="w-full text-sm text-gray-900 pb-2 border-b border-gray-200 bg-transparent focus:outline-none focus:border-gray-400 text-left"
                                      />
                                    </div>
                                  ) : (
                                    <DetailField label="Onboarding Date" value={onboardingDate || "-"} />
                                  )}

                                  {/* ── Expiry Date ────────────────────────────── */}
                                  {activeTab === 'pending' && !ticketDetails?.expiryDate ? (
                                    <div className="flex flex-col">
                                      <label className="text-xs text-gray-500 font-medium mb-2 block text-left">Expiry Date</label>
                                      <input
                                        type="date"
                                        value={expiryDate}
                                        onChange={(e) => setExpiryDate(e.target.value)}
                                        className="w-full text-sm text-gray-900 pb-2 border-b border-gray-200 bg-transparent focus:outline-none focus:border-gray-400 text-left"
                                      />
                                    </div>
                                  ) : (
                                    <DetailField label="Expiry Date" value={expiryDate || "-"} />
                                  )}

                                  <DetailField label="Fleet Status" value={ticketDetails?.fleetStatus} />
                                  <DetailField label="Hub Name" value={ticketDetails?.hubName} />
                                  <DetailField label="Raised By" value={ticketDetails?.raisedBy} />
                                  <DetailField label="OEM Name" value={ticketDetails?.oemName} />

                                  {/* ── UID Image ──────────────────────────────── */}
                                  <div className="flex flex-col">
                                    <div className="text-xs text-gray-500 font-medium mb-2 text-left">UID Image</div>
                                    <div className="flex items-center gap-8 text-left pb-2 border-b border-gray-200">
                                      <span>img.png</span>
                                      {ticketDetails?.uidImageId ? (
                                        <button
                                          type="button"
                                          onClick={handleViewUidImage}
                                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-500 text-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                          aria-label="View UID image"
                                        >
                                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2 17.8435C1.45 17.8435 0.979167 17.6493 0.5875 17.2611C0.195833 16.8728 0 16.4061 0 15.8609V1.98261C0 1.43739 0.195833 0.970652 0.5875 0.582391C0.979167 0.19413 1.45 0 2 0H16C16.55 0 17.0208 0.19413 17.4125 0.582391C17.8042 0.970652 18 1.43739 18 1.98261V15.8609C18 16.4061 17.8042 16.8728 17.4125 17.2611C17.0208 17.6493 16.55 17.8435 16 17.8435H2ZM2 15.8609H16V1.98261H2V15.8609ZM4 13.8783H14C14.2 13.8783 14.35 13.7874 14.45 13.6057C14.55 13.4239 14.5333 13.2504 14.4 13.0852L11.65 9.44217C11.55 9.31 11.4167 9.24391 11.25 9.24391C11.0833 9.24391 10.95 9.31 10.85 9.44217L8.25 12.887L6.4 10.4335C6.3 10.3013 6.16667 10.2352 6 10.2352C5.83333 10.2352 5.7 10.3013 5.6 10.4335L3.6 13.0852C3.46667 13.2504 3.45 13.4239 3.55 13.6057C3.65 13.7874 3.8 13.8783 4 13.8783Z" fill="#1366D9" />
                                          </svg>
                                        </button>
                                      ) : (
                                        <span className="text-sm text-gray-500">No image</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                {activeTab === 'pending' && (
                                  <div className="flex justify-end gap-3 mt-6">
                                    <button
                                      onClick={handleOpenCancelDialog}
                                      className="px-6 py-2 border-2 border-red-500 text-red-500 rounded-full font-medium hover:bg-red-50 transition-colors text-sm"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={handleMapPart}
                                      className="px-6 py-2 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-colors text-sm"
                                    >
                                      Map Part
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))
                  ) : null}
                  {mappingData.length === 0 ||
                    mappingData.filter((item) =>
                      activeTab === 'pending' ? item.partState === 'NOT_WORKING' : item.partState === 'WORKING'
                    ).length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-8 text-center text-gray-500">No data available</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            )}
          </div>

          {/* ✅ Pagination — works for all three tabs */}
          {(activeTab === 'bulk-upload' ? bulkUploadHistory.length > 0 : mappingData.length > 0) && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                {activeTab === 'bulk-upload'
                  ? `Page ${page + 1}`
                  : `Showing page ${page + 1} of ${totalPages}`}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  // ✅ bulk-upload uses hasNextPage flag; others use totalPages
                  disabled={activeTab === 'bulk-upload' ? !hasNextPage : page >= totalPages - 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* UID Image Preview Modal */}
        {imagePreviewUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full mx-4">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900 text-left">UID Image Preview</h2>
                <button type="button" onClick={() => setImagePreviewUrl(null)} className="text-gray-500 hover:text-gray-700 focus:outline-none" aria-label="Close preview">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="p-4 flex items-center justify-center bg-gray-50">
                <img src={imagePreviewUrl} alt="UID" className="max-h-[70vh] w-auto rounded-md border border-gray-200 object-contain" />
              </div>
            </div>
          </div>
        )}

        {/* Cancel Dialog */}
        {showCancelDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 text-left">Add Remarks Before Proceeding.</h2>
                <button type="button" onClick={() => { setShowCancelDialog(false); setCancelRemarks(""); }} className="text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Close dialog">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <textarea
                  value={cancelRemarks}
                  onChange={(e) => setCancelRemarks(e.target.value)}
                  placeholder="Add Remarks"
                  rows={6}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <button
                  onClick={handleCancelTicket}
                  disabled={!cancelRemarks.trim()}
                  className="w-full mt-4 px-6 py-3 bg-blue-600 text-white text-base font-medium rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send back Part
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Snackbar */}
        <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }} variant="filled">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default UIDPartCodeMapping;